import { useAuroraStore } from '../../store/auroraStore'
import { domainSummaries, type DomainSummary } from '../../store/selectors'
import { formatRelative } from '../../lib/dates'
import { domainColor } from '../../lib/rubric'
import { LevelBar } from './LevelBar'
import { TrendBadge } from './TrendBadge'
import { ConfidenceDot } from './ConfidenceDot'

/**
 * The whole map of the territory, at a glance.
 *
 * Every domain is always here, whether or not anything has been observed in it.
 * A grid that grew as observations arrived would quietly reward the parent for
 * logging and hide whole areas of a child's life; a fixed map instead makes
 * "we haven't looked at this yet" a visible, ordinary, blameless state — and
 * an invitation rather than a gap.
 *
 * Colour appears only where it carries meaning: the level bar, and nothing else.
 */
export function DomainGrid({
  onOpen,
  childName,
}: {
  onOpen: (domainKey: string) => void
  childName?: string
}) {
  const domains = useAuroraStore(domainSummaries)

  const measured = domains.filter((d: DomainSummary) => d.observedCount > 0)
  const unmeasured = domains.filter((d: DomainSummary) => d.observedCount === 0)

  return (
    <div className="domaingrid-wrap">
      {measured.length > 0 && (
        <ul className="domaingrid">
          {measured.map((domain: DomainSummary, i: number) => (
            <li key={domain.key} className="reveal" style={{ '--i': i } as React.CSSProperties}>
              <DomainTile domain={domain} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}

      {unmeasured.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h3 className="section-title">Not looked at yet</h3>
          </div>
          <p className="domaingrid-note">
            Nothing has been logged here, so there is nothing to say, which is different from
            saying {childName ?? 'she'} cannot do it. Open one to see what it covers and how to
            check.
          </p>
          <ul className="domaingrid is-quiet">
            {unmeasured.map((domain: DomainSummary) => (
              <li key={domain.key}>
                <DomainTile domain={domain} onOpen={onOpen} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function DomainTile({
  domain,
  onOpen,
}: {
  domain: DomainSummary
  onOpen: (domainKey: string) => void
}) {
  const measured = domain.observedCount > 0

  return (
    <button
      className={`domaintile${measured ? '' : ' is-unmeasured'}`}
      style={{ '--domain-color': domainColor(domain.key, 'var(--accent)') } as React.CSSProperties}
      onClick={() => onOpen(domain.key)}
      aria-label={
        measured
          ? `${domain.name}, level ${domain.level.toFixed(1)} of 5, across ${domain.observedCount} skills`
          : `${domain.name}, not looked at yet`
      }
    >
      <span className="domaintile-head">
        <span className="domaintile-name">{domain.name}</span>
        {measured && <TrendBadge trend={domain.trend} size="sm" showLabel={false} />}
      </span>

      {measured ? (
        <>
          <span className="domaintile-figure">
            <span className="domaintile-level tnum">{domain.level.toFixed(1)}</span>
            <ConfidenceDot
              confidence={domain.confidence}
              observationCount={domain.evidenceCount}
              lastObservedAt={domain.lastObservedAt}
              size="sm"
              interactive={false}
            />
          </span>
          <LevelBar
            level={domain.level}
            domainKey={domain.key}
            confidence={domain.confidence}
            size="sm"
          />
          <span className="domaintile-foot">
            {domain.observedCount} of {domain.skillCount} skills
            {domain.lastObservedAt ? ` · ${formatRelative(domain.lastObservedAt)}` : ''}
          </span>
        </>
      ) : (
        <>
          <span className="domaintile-figure">
            <span className="domaintile-level is-blank" aria-hidden="true">
              —
            </span>
          </span>
          <LevelBar level={0} domainKey={domain.key} size="sm" />
          <span className="domaintile-foot">{domain.skillCount} skills to explore</span>
        </>
      )}
    </button>
  )
}
